import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBullhorn, faXmark } from '@fortawesome/free-solid-svg-icons';
import CgDrawer, { CgDrawerClose, CgDrawerTitle } from 'modules/Common/layout/CgDrawer';
import CgButton from 'modules/Common/ui/CgButton';
import { useBranding } from 'modules/Common/hooks/useBranding';
import ReleaseNoteList from './ReleaseNoteList';
import { useWhatsNewUnread } from '../hooks/useWhatsNewUnread';
import { useWhatsNewAutoOpen } from '../hooks/useWhatsNewAutoOpen';

/**
 * The drawer's frame, with no state of its own.
 *
 * Header: an icon badge, an eyebrow, the brand-interpolated title and a
 * subtitle that counts the releases on show. Body: the shared timeline
 * (ReleaseNoteList, D10). Footer: one primary "Got it" — it goes through the
 * same `onOpenChange(false)` as the close button and Escape, so every exit
 * marks the notes read (see the default export).
 *
 * Exported for the stateful default below and for anything that needs to
 * render the drawer with releases in hand (previews, harnesses).
 */
export function WhatsNewDrawerView({ open, onOpenChange, releases }) {
  const { t: tApp } = useTranslation('app');
  const { t: tCommon } = useTranslation('common');
  const brandName = useBranding((s) => s.name);
  const count = releases.length;

  const header = (
    <div className="relative overflow-hidden border-b border-slate-200 bg-gradient-to-b from-primaryColor/[0.07] to-transparent px-6 pb-5 pt-5">
      <div className="flex items-start gap-4">
        <span
          aria-hidden="true"
          className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primaryColor text-white shadow-sm"
        >
          <FontAwesomeIcon
            icon={faBullhorn}
            className="h-4 w-4"
          />
        </span>
        <div className="min-w-0 flex-1">
          <p className="mb-1 text-[11px] font-semibold uppercase leading-4 tracking-[0.08em] text-primaryColor-dark">
            {tApp('whatsNew.eyebrow', 'Release notes')}
          </p>
          <CgDrawerTitle className="m-0 text-lg font-semibold leading-6 tracking-tight text-slate-900">
            {tApp('whatsNew.title', "What's new in {{brand}}", { brand: brandName })}
          </CgDrawerTitle>
          <p className="mb-0 mt-1 text-sm leading-5 text-slate-500">
            {tApp(
              'whatsNew.drawerSubtitleCount',
              '{{count}} new releases since you were last here.',
              { count },
            )}
          </p>
        </div>
        <CgDrawerClose
          aria-label={tCommon('common:actions.close', 'Close')}
          className="-me-2 -mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-900/5 hover:text-slate-700"
        >
          <FontAwesomeIcon icon={faXmark} />
        </CgDrawerClose>
      </div>
    </div>
  );

  const footer = (
    <div className="flex items-center justify-end">
      <CgButton
        variant="primary"
        size="md"
        label={tApp('whatsNew.gotIt', 'Got it')}
        onClick={() => onOpenChange(false)}
        className="min-w-[7rem] shadow-none"
      />
    </div>
  );

  return (
    <CgDrawer
      open={open}
      onOpenChange={onOpenChange}
      header={header}
      footer={footer}
      width={520}
      // Non-modal: no overlay, no scroll lock, no focus trap. An announcement
      // the user did not ask for must not seize the page — they dismiss it by
      // carrying on working. CgDrawer already blocks outside-click closing in
      // this mode, so the close button, Escape and "Got it" are the deliberate
      // exits.
      modal={false}
      placement="right"
      bodyClassName="px-6 py-6"
      footerClassName="px-6 py-4 bg-white"
    >
      <ReleaseNoteList releases={releases} />
    </CgDrawer>
  );
}

/**
 * The post-login What's New drawer (D5, D11).
 *
 * Mounted once in AppLayout, so it is present on every authenticated route and
 * on none of the outside routes — see the phase-3 doc for why AppLayout and not
 * _routes.jsx, which despite appearances also renders on `/onboarding/wizard`.
 *
 * Renders nothing at all until the backend serves release notes (D3).
 */
export default function WhatsNewDrawer() {
  const { unread, unreadCount, markAllRead } = useWhatsNewUnread();
  const [open, setOpen] = useWhatsNewAutoOpen(unreadCount);

  // Snapshot the unread list at the moment the drawer opens.
  //
  // Closing marks everything read, which empties `unread` on the same tick.
  // Rendering straight from `unread` would therefore blank the drawer's content
  // and unmount it before Radix could play the slide-out — the user would see
  // the text vanish, then the panel. The snapshot keeps what they were reading
  // on screen until the animation finishes.
  const [shownReleases, setShownReleases] = useState([]);
  useEffect(() => {
    if (open && unread.length) setShownReleases(unread);
    // `unread` is intentionally absent: the snapshot must not track later
    // changes, which is the entire point of taking one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleOpenChange = (next) => {
    setOpen(next);
    // Closing IS reading. A user who dismisses the drawer has decided the news
    // is not for them; re-prompting on the next navigation would be nagging.
    if (!next) markAllRead();
  };

  // Only the unread releases — this surface is "what's new since you last
  // looked", not the full history. The org-profile tab (D9) holds the whole
  // catalogue.
  if (!shownReleases.length) return null;

  return (
    <WhatsNewDrawerView
      open={open}
      onOpenChange={handleOpenChange}
      releases={shownReleases}
    />
  );
}
